import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import Button from 'flarum/common/components/Button';
import TextEditor from 'flarum/common/components/TextEditor';

/*
 * Forum phone upload review, item 2 (2026-10-30; Forum_Abstract.md, "Phone upload review"). fof/upload's media manager
 * draws its own view() without core's Modal-close, so on a phone the only exit was Cancel at the bottom, and its upload
 * button is an icon with the label hidden. Here, without touching vendor:
 * - a close (X) in the phone header bar, top left, where core modals keep theirs (forum.less hides it from 768px,
 *   where Esc and the backdrop already close the manager);
 * - the manager's upload button reads "Upload photo", without a tooltip (forum.less makes it a full-width row at
 *   phone width);
 * - focus moves into the manager when it opens. Flarum hides #app from assistive tech behind a modal, and the
 *   composer button that opened it still held focus, which Chrome reports as a blocked aria-hidden.
 * Items 3 to 5 extend the same components; see their comments below.
 * fof/upload is looked up at initializer time (every extension's module has run by then); absent, this is a no-op.
 */
export default function installPhoneUpload() {
  const fof = flarum.extensions['fof-upload'];
  const FileManagerModal = fof?.components?.FileManagerModal;
  const UploadButton = fof?.components?.UploadButton;
  const FileManagerButton = fof?.components?.FileManagerButton;
  if (!FileManagerModal || !UploadButton) return;

  extend(FileManagerModal.prototype, 'view', function (vdom) {
    const content = (vdom.children || []).find((child) => child?.attrs?.className === 'Modal-content');
    if (!content || !Array.isArray(content.children)) return;

    // Core pins every .App-backControl into the phone header bar (position/top !important); the upload row is placed
    // by forum.less instead. Core styles that class at phone width only, so desktop is unaffected.
    const buttons = content.children.find((child) => child?.attrs?.className?.includes('fof-modal-buttons'));
    if (buttons) buttons.attrs.className = 'fof-modal-buttons';

    content.children.unshift(
      <div className="Modal-close App-backControl CitRegFileManager-close">
        <Button icon="fas fa-times" className="Button Button--icon Button--link" aria-label="Close" onclick={() => this.hide()} />
      </div>
    );
  });

  extend(FileManagerModal.prototype, 'oncreate', function () {
    const active = document.activeElement;
    if (active && this.element && !this.element.contains(active)) {
      // The close is hidden from 768px and cannot take focus there; blur instead.
      this.element.querySelector('.CitRegFileManager-close .Button')?.focus({ preventScroll: true });
      if (!this.element.contains(document.activeElement)) active.blur();
    }
  });

  // Item 3: the picker offers only what the server takes (the Admin allowlist, from extend.php / UploadAccept.php),
  // on the composer's upload button and the manager's alike.
  extend(UploadButton.prototype, 'view', function (vdom) {
    if (!Array.isArray(vdom.children)) return;

    const accept = app.forum.attribute('citregUploadAccept');
    const input = vdom.children.find((child) => child?.tag === 'form')?.children?.find((child) => child?.tag === 'input');
    if (input && accept) input.attrs.accept = accept;

    if (this.attrs.uploader.uploading) return;

    if (!this.isMediaUploadButton) {
      // Item 5: the composer's direct upload control. "Add photo" as its tooltip on desktop; on phones a camera icon
      // and the words themselves (forum.less shows the label and moves the button to the front of the toolbar), as a
      // plain Button: a visible label needs no tooltip, and TextEditorButton's lingers after a tap.
      vdom.attrs.title = 'Add photo';
      vdom.attrs.className += ' CitRegAddPhoto';
      vdom.children.unshift(<span className="CitRegAddPhoto-label">Add photo</span>);
      if (isPhone()) {
        vdom.attrs.icon = 'fas fa-camera';
        // Core hides every label inside .Button--icon.
        vdom.attrs.className = vdom.attrs.className.replace(/\bButton--(icon|link)\b/g, '');
        vdom.tag = Button;
      }
      return;
    }

    const i = vdom.children.findIndex((child) => child?.attrs?.className === 'Button-label');
    if (i !== -1) vdom.children[i] = <span className="Button-label">Upload photo</span>;
    // A plain Button, not fof's TextEditorButton: the label says it all, and the latter's tooltip lingered after a tap,
    // covering the tabs (operator's review, 2026-10-30).
    vdom.tag = Button;
  });

  // Item 3: the composer's media manager button showed a photo-and-video icon; the Forum takes photos only.
  if (FileManagerButton) {
    extend(FileManagerButton.prototype, 'view', function (vdom) {
      const button = (vdom.children || []).find((child) => child?.attrs?.icon);
      if (button) button.attrs.icon = 'fas fa-images';
    });
  }

  // Item 5: on phones "Add photo" leads the toolbar (the submit item is pinned in the header there, so it only moves
  // ahead of Preview and the media manager). Desktop keeps Post Reply first.
  extend(TextEditor.prototype, 'controlItems', function (items) {
    if (isPhone() && items.has('fof-upload')) items.setPriority('fof-upload', 10);
  });

  // Item 5: the composer toolbar is one row that scrolls sideways on phones, with nothing to say so (half its controls
  // start past the edge of a 375px screen). While more lies to the right, the editor carries CitRegToolbar--more and
  // forum.less fades the bar's right end.
  // The class goes on the bar itself (its className never changes, so Mithril leaves it alone).
  extend(TextEditor.prototype, 'oncreate', function () {
    const bar = this.element.querySelector('.TextEditor-controls');
    if (!bar) return;
    this.citregToolbarCue = () => bar.classList.toggle('CitRegToolbar--more', bar.scrollLeft + bar.clientWidth < bar.scrollWidth - 2);
    bar.addEventListener('scroll', this.citregToolbarCue, { passive: true });
    window.addEventListener('resize', this.citregToolbarCue);
    this.citregToolbarCue();
  });
  extend(TextEditor.prototype, 'onupdate', function () {
    this.citregToolbarCue?.();
  });
  extend(TextEditor.prototype, 'onremove', function () {
    if (this.citregToolbarCue) window.removeEventListener('resize', this.citregToolbarCue);
  });
}

function isPhone() {
  return window.matchMedia('(max-width: 767px)').matches;
}
